import PubNub from "pubnub";
import QRCode from "qrcode";
import {
  success,
  toast,
  confirm,
  successRestaurantCheckout,
  shouldPrintFromPaymentAlert,
} from "@/controllers/alert";
import { mapGetters, mapActions, mapMutations } from "vuex";
import { tableStatusList, pubnubConfig, ActionList } from "@/utils/mock";
import PrinterService from "@/services/printerService";
import { mainStore } from "@/stores/modules/cart";
import { getLastMutationAt } from "@/utils/fetchCache";

// Table Management Mixin - Shared logic for both mobile and desktop table views
export const tableManagementMixin = {
  data() {
    return {
      pubnub: new PubNub(pubnubConfig()),
      show_qr: false,
      qr: "",
      dialogMoveMerge: false,
      print: false,
      update: false,
      dialogAdd: false,
      dialog: false,
      reservedModal: false,
      paymentModal: false,
      billDetail: false,
      inputValue: "",
      search: "",
      note: "",
      table_id: "",
      form: {
        name: "",
        note: "",
        status: "",
        zone_id: null,
        seats: 4,
      },
      total_price: 0,
      status: "",
      status_filter: "",
      zone_filter: "",
      ticket_id: "",
      status_list: tableStatusList(),
      zone_list: [],
      current_page: 1,
      per_page: 30,
      items: {
        data: [],
        last_page: 0,
      },
      printBill: {},
      actionList: ActionList(),
      order_table: {},
      selectedItemsForCheckout: null,
      activeTable: {
        table: {
          name: "",
        },
      },
      order_id: "",
      dragEnabled: false,
      isDragging: false,
      draggedTable: null,
      tablePositions: [],
      draggableTables: [],
      type: "move",
      statistics: {
        total: 0,
        available: 0,
        busy: 0,
        reserved: 0,
        draft: 0,
        completed: 0,
      },
      statisticsArray: [
        {
          key: "total",
          label: "total_tables",
          icon: "fas fa-table",
          color: "primary",
        },
        {
          key: "available",
          label: "available",
          icon: "fas fa-layer-group",
          color: "green",
        },
        {
          key: "busy",
          label: "busy",
          icon: "fas fa-user-friends",
          color: "orange",
        },
        {
          key: "reserved",
          label: "reserved",
          icon: "fas fa-calendar-alt",
          color: "amber",
        },
      ],
      activeStatFilter: "",
      selectedTable: null,
      loading: false,
      hasMore: true,
      activeAction: null,
      loadingTableDetails: false,
      isLoading: false,
      checkbox: false,
      allSelected: false,
      discountModal: false,
      discountType: "percentage",
      discountValue: "0",
      appliedDiscount: null,
      moveOrderModal: false,
      selectedMoveTargetTable: null,
      allTablesForMove: [],
      loadingTablesForMove: false,
      moveOrderSearch: "",
      moveOrderStatusFilter: "",
      moveOrderZoneFilter: "",
      isMergeOperation: false,
      currentTicketId: null,
      qrCodeData: null,
      defineStore: mainStore(),
      actionHandler: "payment",
      leftoverCustomerModal: false,
      leftoverQuantityDialog: false,
      leftoverQuantityItems: [],
      pendingLeftoverItems: [],
      leftoverItemsManagementDialog: false,
      leftoverItemsList: [],
      leftoverSearchQuery: "",
      leftoverCustomerFilter: null,
      leftoverDateFilter: null,
      waiterPubnubChannel: null,
      /** BUFFET: app-custom-dialog style picker from desktop tables */
      buffetSetupModal: false,
      buffetSetupInitialLines: [],
    };
  },

  async created() {
    await this.fetchZones();
    await this.fetchItem();
    await this.fetchStatistics();
  },

  mounted() {
    this.$nextTick(() => {
      if (this.$el && this.$el.querySelector) {
        const tableCardsContainer = this.$el.querySelector(
          ".table-cards-container"
        );
        if (tableCardsContainer) {
          tableCardsContainer.addEventListener("scroll", this.handleScroll);
        }
      }
    });

    this.subscribeWaiter();
  },

  beforeDestroy() {
    if (this.$el && this.$el.querySelector) {
      const tableCardsContainer = this.$el.querySelector(
        ".table-cards-container"
      );
      if (tableCardsContainer) {
        tableCardsContainer.removeEventListener("scroll", this.handleScroll);
      }
    }

    this.unsubscribePubNub();
  },

  // Table pages are kept alive (see utils/keepAlive.js). The PubNub subscription stays open while
  // hidden (no leave/subscribe churn) but only flags "dirty" — no toast/sound/fetch in the
  // background. On re-entry we refetch only if something changed: a PubNub message arrived, a write
  // succeeded elsewhere (order added from /sale, payment, ...), or we were away for a long time.
  deactivated() {
    this._tblDeactivated = true;
    this._tblLeftAt = Date.now();
    this._tblDirty = false;
  },
  activated() {
    if (!this._tblDeactivated) return; // first activation: created()/mounted() did the setup
    this._tblDeactivated = false;
    const away = Date.now() - (this._tblLeftAt || 0);
    const changed =
      this._tblDirty ||
      getLastMutationAt() > (this._tblLeftAt || 0) ||
      away > 5 * 60 * 1000; // PubNub may have dropped while hidden
    this._tblDirty = false;
    if (!changed) return;
    // quiet refresh: keeps the cards on screen (no skeleton flash)
    this.fetchItem(false, true);
    this.fetchStatistics();
    if (this.selectedTable && this.selectedTable.id) {
      this.updateSingleTable(this.selectedTable.id);
    }
  },

  computed: {
    getStatColor() {
      return (color) => {
        const colorMap = {
          primary: "#1976D2",
          green: "#4CAF50",
          orange: "#FF9800",
          amber: "#FFC107",
          blue: "#2196F3",
          success: "#4CAF50",
        };
        return colorMap[color] || color;
      };
    },

    canRepositionTables() {
      if (!this.user) return false;
      return [1, 2, 3, 4].includes(this.user.role);
    },

    sortedTables() {
      if (!this.items.data) return [];
      const tables = [...this.items.data];
      return tables.sort((a, b) => {
        const aPosition =
          a.position_index !== undefined ? a.position_index : tables.indexOf(a);
        const bPosition =
          b.position_index !== undefined ? b.position_index : tables.indexOf(b);
        return aPosition - bPosition;
      });
    },

    ...mapGetters({
      overlay: "index/overlay",
      tables: "index/tables",
      user: "auth/user",
      activeCurrencies: "index/activeCurrencies",
      tax_rate: "auth/tax_rate",
    }),

    translatedStatusList() {
      return this.status_list.map((item) => ({
        ...item,
        text: this.$t(item.text),
      }));
    },

    bill_item() {
      const msg = this.$t("bill_item");
      if (this.order_table && this.order_table.table?.name) {
        return msg + " " + this.order_table.table?.name;
      }
      return msg;
    },

    tableDetail() {
      return this.$t("table");
    },

    tableAction() {
      const msg = this.order_table ? "merge_table" : "move_table";
      return (
        this.$t(msg) +
        " " +
        (this.activeTable?.table?.name || this.activeTable?.name || "")
      );
    },

    statusTitle() {
      if (this.status === "cancel") {
        return "reason_cancel";
      }
      return "choose_table_for_order";
    },

    titleStatus() {
      return this.update ? "update" : "new_data";
    },

    tableNameError() {
      if (!this.$v.form || !this.$v.form.name) return [];
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      const msg = this.$t("validation_required");
      !this.$v.form.name.required && errors.push(msg);
      return errors;
    },

    hasSelectedItems() {
      return (
        this.order_table &&
        this.order_table.history &&
        this.order_table.history.some((item) => item.selected)
      );
    },

    allSelectedState: {
      get() {
        return this.allSelected;
      },
      set(value) {
        this.allSelected = value;
        if (this.order_table && this.order_table.history) {
          this.order_table.history.forEach((item) => {
            this.$set(item, "selected", value);
          });
        }
      },
    },

    translatedZoneList() {
      const allOption = {
        id: "",
        name: this.$t("all"),
        description: "Show all tables",
      };
      return [
        allOption,
        ...this.zone_list.map((zone) => ({
          ...zone,
          name: zone.name,
          id: zone.id,
        })),
      ];
    },

    hasSelectedItemsForMove() {
      return this.order_table?.history?.some((item) => item.selected) || false;
    },

    isTableSelected() {
      return !!this.selectedTable;
    },

    hasDeliveredItems() {
      if (!this.order_table || !this.order_table.history) {
        return false;
      }
      return this.order_table.history.some(
        (item) => item.status === "delivered"
      );
    },

    /**
     * Checkout is blocked until kitchen lines are delivered — except BUFFET-only
     * tickets (no product lines), where payment is allowed on plan totals alone.
     */
    tableCheckoutBlocked() {
      if (!this.order_table || !this.order_table.id) {
        return true;
      }
      const hist = this.order_table.history || [];
      if (hist.length === 0) {
        if (this.isBuffet && (this.order_table.buffetPlans || []).length > 0) {
          return false;
        }
        return true;
      }
      return !this.hasDeliveredItems;
    },

    hasTableOrders() {
      if (!this.order_table || !this.order_table.id) {
        return false;
      }
      if (this.order_table.history && this.order_table.history.length > 0) {
        return true;
      }
      if (
        this.isBuffet &&
        this.order_table.buffetPlans &&
        this.order_table.buffetPlans.length > 0
      ) {
        return true;
      }
      return false;
    },

    isTableAvailable() {
      return this.selectedTable?.status === "available" || false;
    },

    allItemsDelivered() {
      if (
        !this.order_table ||
        !this.order_table.history ||
        this.order_table.history.length === 0
      ) {
        return false;
      }
      return this.order_table.history.every(
        (item) => item.status === "delivered"
      );
    },

    allItemsCanceled() {
      if (
        !this.order_table ||
        !this.order_table.history ||
        this.order_table.history.length === 0
      ) {
        return false;
      }
      return this.order_table.history.every((item) => item.status === "cancel");
    },

    buttonDisabledStates() {
      const isTableSelected = this.isTableSelected;
      const isAvailable = this.isTableAvailable;
      const hasOrders = this.hasTableOrders;
      const hasOpenTicket = !!(
        this.order_table &&
        this.order_table.id
      );

      return {
        print_kitchen: !isTableSelected || isAvailable || !hasOrders,
        close_table: !isTableSelected || isAvailable || !hasOrders,
        merge_table: !isTableSelected || isAvailable || !hasOrders,
        print_receipt: !isTableSelected || !hasOrders,
        discount: !isTableSelected || !hasOrders,
        checkout:
          !isTableSelected || !hasOrders || this.tableCheckoutBlocked,
        add_order: !isTableSelected,
        confirm_close: !isTableSelected || isAvailable || !hasOrders,
        move_order:
          !isTableSelected ||
          isAvailable ||
          !hasOrders ||
          !this.hasSelectedItemsForMove,
        open_table: !isTableSelected || !isAvailable,
        edit_buffet_plan:
          !isTableSelected || isAvailable || !hasOpenTicket,
        close_table_buffet:
          !isTableSelected || isAvailable || !hasOpenTicket,
      };
    },

    selectedItemsForDiscount() {
      const selectedItems =
        this.order_table.history?.filter((item) => item.selected) || [];
      return selectedItems.length > 0
        ? selectedItems
        : this.order_table.history || [];
    },

    totalBeforeDiscount() {
      return this.selectedItemsForDiscount.reduce(
        (sum, item) => sum + (item.total_price || 0),
        0
      );
    },

    totalPriceBeforeDiscount() {
      if (this.order_table?.total_price != null) {
        return Math.max(Number(this.order_table.total_price || 0), 0);
      }
      return Math.max(Number(this.total_price || 0), 0);
    },

    discountAmount() {
      return this.appliedDiscount?.amount || 0;
    },

    subTotalPrice() {
      const beforeDiscount = this.totalPriceBeforeDiscount;
      return Math.max(beforeDiscount - this.discountAmount, 0);
    },

    vatEnabled() {
      return !!this.user?.stores?.tax_include;
    },

    vatRate() {
      // Use Vuex getter with default 10% tax rate
      return Number(this.tax_rate || 10);
    },

    vatAmount() {
      if (!this.vatEnabled || this.vatRate <= 0) return 0;
      const taxable = this.subTotalPrice; // Use discounted amount
      return (taxable * this.vatRate) / 100;
    },

    /** VAT-inclusive total due — single source for payment pad and checkout API body */
    amountDue() {
      const subtotal = this.subTotalPrice;
      if (!this.vatEnabled) {
        return subtotal;
      }
      const ticketTax = Number(this.order_table?.tax_amount);
      if (!Number.isNaN(ticketTax) && ticketTax >= 0) {
        return subtotal + ticketTax;
      }
      return subtotal + this.vatAmount;
    },

    grandTotalWithVat() {
      return this.amountDue;
    },

    currencySymbol() {
      const activeCurrencies = this.$store.getters["index/activeCurrencies"];
      if (activeCurrencies && activeCurrencies.length > 0) {
        const lak = activeCurrencies.find((c) => c.code === "LAK");
        if (lak) {
          return { symbol: lak.symbol || "₭", code: "LAK" };
        }
        return {
          symbol: activeCurrencies[0].symbol || "₭",
          code: activeCurrencies[0].code,
        };
      }
      return { symbol: "₭", code: "LAK" };
    },

    tableList() {
      return this.tables || [];
    },

    actualProductCurrency() {
      if (
        !this.order_table ||
        !this.order_table.history ||
        this.order_table.history.length === 0
      ) {
        return { code: "LAK", symbol: "₭" };
      }

      const firstItem = this.order_table.history[0];
      if (firstItem && firstItem.currency) {
        return {
          code: firstItem.currency.code,
          symbol:
            firstItem.currency.symbol ||
            (firstItem.currency.code === "THB" ? "฿" : "₭"),
        };
      }

      return { code: "LAK", symbol: "₭" };
    },

    convertedTotalPrice() {
      const currency = this.defineStore.currency;

      if (
        !currency ||
        currency.code === "LAK" ||
        !currency.store_exchange_rate
      ) {
        return this.total_price;
      }

      const exchangeRate = parseFloat(currency.store_exchange_rate) || 1;
      if (exchangeRate <= 0) {
        return this.total_price;
      }

      const converted = this.total_price / exchangeRate;

      console.log("💱 [tables] Converted total price", {
        originalTotal: this.total_price,
        currency: currency.code,
        exchangeRate: exchangeRate,
        converted: converted,
      });

      return converted;
    },

    availableTablesForMove() {
      let tables =
        this.allTablesForMove && Array.isArray(this.allTablesForMove)
          ? this.allTablesForMove.filter(
              (table) =>
                table && table.id && table.id !== this.selectedTable?.id
            )
          : [];

      if (this.moveOrderSearch && this.moveOrderSearch.length > 0) {
        const searchTerm = this.moveOrderSearch.toLowerCase();
        tables = tables.filter(
          (table) =>
            (table.name && table.name.toLowerCase().includes(searchTerm)) ||
            (table.zone &&
              table.zone.name &&
              table.zone.name.toLowerCase().includes(searchTerm))
        );
      }

      if (this.moveOrderStatusFilter) {
        tables = tables.filter(
          (table) => table.status === this.moveOrderStatusFilter
        );
      }

      if (this.moveOrderZoneFilter) {
        tables = tables.filter(
          (table) => table.zone_id === this.moveOrderZoneFilter
        );
      }

      return tables;
    },
  },

  watch: {
    moveOrderModal(newVal) {
      if (!newVal) {
        this.moveOrderSearch = "";
        this.moveOrderStatusFilter = "";
        this.moveOrderZoneFilter = "";
        this.selectedMoveTargetTable = null;
        this.isMergeOperation = false;
      }
    },

    sortedTables: {
      handler(newTables) {
        this.draggableTables = [...newTables];
      },
      immediate: true,
    },

    status_filter() {
      this.selectedTable = null;
      this.order_table = {};
      // activeStatFilter is managed by filterByStatus method, don't reset it here
      this.fetchItem();
      this.fetchStatistics();
    },

    zone_filter() {
      this.selectedTable = null;
      this.order_table = {};
      this.fetchItem();
    },

    search() {
      this.selectedTable = null;
      this.order_table = {};
      this.fetchItem();
    },

    actionHandler: {
      handler(newHandler, oldHandler) {
        console.log("🔄 ActionHandler changed:", { newHandler, oldHandler });
        if (
          newHandler === "payment" &&
          oldHandler === "exchangeRate" &&
          this.pendingConvertedAmount &&
          this.pendingCurrencyData
        ) {
          console.log("🔄 Applying pending conversion data:", {
            amount: this.pendingConvertedAmount,
            currency: this.pendingCurrencyData.code,
          });
          this.$nextTick(() => {
            this.trySetExternalCurrency(this.pendingCurrencyData, 0);
            this.trySetExternalPaymentAmount(this.pendingConvertedAmount, 0);
          });
        }
      },
    },
  },

  methods: {
    subscribeWaiter() {
      const waiterChannel = `${this.user.stores.code}_waiter`;
      this.waiterPubnubChannel = waiterChannel;

      this._waiterPubNubListener = {
        message: (event) => {
          if (event.channel !== waiterChannel) {
            return;
          }

          let msgContent = event.message;
          let tableId = null;

          if (typeof msgContent === "object" && msgContent.message) {
            tableId = msgContent.table_id;
            msgContent = msgContent.message;
          }

          // hidden (kept-alive) page: just remember something changed, refresh on re-entry
          if (this._tblDeactivated) {
            this._tblDirty = true;
            return;
          }

          toast(this, msgContent, "success");
          Help.playAudio();

          if (tableId) {
            console.log("🔔 [tables] Targeted update for table:", tableId);
            this.updateSingleTable(tableId);
            this.fetchStatistics();
          } else {
            console.log("🔔 [tables] Full update");
            this.fetchItem();
            this.fetchStatistics();
          }
        },
      };
      this.pubnub.addListener(this._waiterPubNubListener);
      this.pubnub.subscribe({ channels: [waiterChannel] });
    },

    unsubscribePubNub() {
      if (this._ticketPubNubListener) {
        try {
          this.pubnub.removeListener(this._ticketPubNubListener);
        } catch (error) {
          console.warn("Error removing ticket PubNub listener:", error);
        }
        this._ticketPubNubListener = null;
      }

      if (this._waiterPubNubListener) {
        try {
          this.pubnub.removeListener(this._waiterPubNubListener);
        } catch (error) {
          console.warn("Error removing waiter PubNub listener:", error);
        }
        this._waiterPubNubListener = null;
      }

      if (this.waiterPubnubChannel) {
        try {
          this.pubnub.unsubscribe({ channels: [this.waiterPubnubChannel] });
        } catch (error) {
          console.warn("Error unsubscribing from waiter channel:", error);
        }
        this.waiterPubnubChannel = null;
      }

      if (this.currentTicketId) {
        try {
          const ticketChannel = String(this.currentTicketId);
          this.pubnub.unsubscribe({ channels: [ticketChannel] });
          console.log(
            "🧹 [tables] Unsubscribed from QR payment channel:",
            ticketChannel
          );
        } catch (error) {
          console.warn("Error unsubscribing from QR payment channel:", error);
        }
      }
    },

    ...mapActions({
      fetch: "index/fetch",
      fetchActiveCurrencies: "index/fetchActiveCurrencies",
    }),
    ...mapMutations({
      SET_OVERLAY: "index/SET_OVERLAY",
    }),

    /**
     * Buffet line: product from an is_buffet_included category, charged on the plan, not the line.
     */
    isBuffetIncludedLine(item) {
      if (!this.isBuffet) {
        return false;
      }
      if (Number(item?.price_sale ?? 0) !== 0) {
        return false;
      }
      const flag = item?.product_id?.category?.is_buffet_included;
      return flag === true || flag === 1;
    },

    orderStatusColor(status) {
      const colors = {
        pending: "orange",
        cooking: "blue",
        ready: "green",
        overdue: "deep-orange",
        cancel: "red",
        delivered: "success",
        done: "success",
        new: "primary",
        preparing: "blue",
      };
      return colors[status] || "grey";
    },

    async updateSingleTable(tableId) {
      if (!tableId) return;

      const index = this.items.data.findIndex((t) => t.id === tableId);
      if (index === -1) {
        // Table not found in current list, maybe fall bck to full fetch
        this.fetchItem();
        return;
      }

      try {
        const { data } = await this.$http.get(
          `get_table_order/${tableId}?check=1`
        );

        if (data && data.status && data.data) {
          const ticketData = data.data;
          const tableData = ticketData.table;

          // Update the table in the list with new status & active ticket
          const updatedTable = {
            ...this.items.data[index],
            status: tableData.status,
            active_ticket: {
              id: ticketData.id,
              total_price: ticketData.total_price,
              is_new_order: ticketData.is_new_order,
              txn: ticketData.code,
              status: ticketData.status,
            },
          };

          // Update items.data
          this.$set(this.items.data, index, updatedTable);

          // Update draggableTables
          if (this.draggableTables) {
            const dragIndex = this.draggableTables.findIndex(
              (t) => t.id === tableId
            );
            if (dragIndex !== -1) {
              this.$set(this.draggableTables, dragIndex, updatedTable);
            }
          }

          // If this is the currently selected table, reload its details too
          if (this.selectedTable && this.selectedTable.id === tableId) {
            // We can pass the updated table, but loadTableDetails calls API anyway.
            // Ideally we should just update local state, but loadTableDetails does good job.
            // But we want to avoid flicker.
            // Actually loadTableDetails calls get_table_order again.
            // We just called it. We can optimize loadTableDetails to accept data?
            // For now, let's just let it be, or manually update order_table
            this.order_table = this.normalizeOrderTablePayload(ticketData);
            this.loadingTableDetails = false;
            this.hydrateBuffetPiniaFromOrderTable();
            // Note: loadTableDetails also handles some defaults.
          }
        }
      } catch (error) {
        console.log("Error updating single table:", error);
      }
    },

    async fetchItem(append = false, silent = false) {
      try {
        if (!append && !silent) {
          this.current_page = 1;
          this.items.data = [];
        }
        if (silent) this.current_page = 1;

        if (!silent) this.loading = true;
        const params = `per_page=${this.per_page}&page=${this.current_page}&q=${this.search}&status=${this.status_filter}&zone_id=${this.zone_filter}`;
        const { data } = await this.$http.get(`store_table_page?${params}`);

        if (append) {
          this.items.data = [...this.items.data, ...data.data.data];
        } else {
          this.items = data.data;
        }

        this.draggableTables = [...this.sortedTables];

        this.hasMore = this.current_page < data.data.last_page;
        this.loading = false;
      } catch (error) {
        console.log("error", error);
        this.loading = false;
      }
    },

    async fetchStatistics() {
      try {
        const { data } = await this.$http.get("table_statistics");
        this.statistics = data.data;
      } catch (error) {
        console.log("error", error);
      }
    },

    onCurrencyChange(currency) {
      try {
        console.log("[tables] currency-change event ->", currency);
        if (currency && currency.store_exchange_rate) {
          const rate = parseFloat(currency.store_exchange_rate);
          const total = this.total_price;
          let converted = rate > 0 ? total / rate : total;
          converted = Math.round(converted * 100) / 100;
          console.log(
            "[tables] total:",
            total,
            "rate:",
            rate,
            "converted:",
            converted
          );
        }
        this.defineStore.setCurrency(currency);
      } catch (e) {
        console.warn("[tables] onCurrencyChange error", e);
      }
    },

    async fetchZones() {
      try {
        const { data } = await this.$http.get("table_zones");
        this.zone_list = data.data;
      } catch (error) {
        console.log("error", error);
      }
    },

    filterByStatus(status) {
      if (status === "" || status === "total") {
        this.activeStatFilter = "";
        this.status_filter = "";
      } else {
        this.activeStatFilter = status;
        this.status_filter = status;
      }
      this.selectedTable = null;
      this.order_table = {};
      this.fetchItem();
      this.fetchStatistics();
    },

    async selectTable(table) {
      // Always open bottom sheet when selecting a table on mobile
      if (this.showTableDetails !== undefined) {
        // If clicking the same table, toggle the bottom sheet
        if (this.selectedTable && this.selectedTable.id === table.id) {
          this.showTableDetails = !this.showTableDetails;
          if (!this.showTableDetails) {
            this.selectedTable = null;
            this.order_table = {};
          }
          return;
        }
      }

      this.selectedTable = table;
      await this.loadTableDetails(table);

      if (this.showTableDetails !== undefined) {
        this.showTableDetails = true;
      }

      this.updateTableNewOrderStatus(table.id, 0);
    },

    async loadTableDetails(table) {
      this.loadingTableDetails = true;
      try {
        const { data } = await this.$http.get(
          `get_table_order/${table.id}?check=1`
        );

        if (data && data.status && data.data) {
          this.order_table = this.normalizeOrderTablePayload(data.data);
          if (this.order_table.history) {
            this.order_table.history.forEach((item) => {
              this.$set(item, "selected", false);
            });
          }
          this.allSelected = false;
          this.hydrateBuffetPiniaFromOrderTable();

          // Mirror table order -> customer screen (same PC, other tab/window)
          try {
            const mappedCart = (this.order_table.history || []).map((h) => {
              const product = h.product_id || {};
              const currency =
                (product.currency && typeof product.currency === "object"
                  ? product.currency
                  : null) ||
                (h.currency && typeof h.currency === "object" ? h.currency : null);
              const safeCategory =
                product.category && typeof product.category === "object"
                  ? product.category
                  : { en_name: "", la_name: "" };

              return {
                id: product.id ?? h.product_id?.id ?? h.id,
                qty: Number(h.qty || 0),
                price: Number(h.total_price ?? (h.price_sale || 0) * (h.qty || 0)),
                item: {
                  ...product,
                  category: safeCategory,
                },
                variant: h.product_variant || null,
                product_variant_id: h.product_variant_id || null,
                currency: currency,
                exchange_rate: 1,
              };
            });

            this.defineStore.setCart(mappedCart);
            this.defineStore.setCustomerScreenOrderMeta({
              tableId: table.id,
              tableName: table.name,
              ticketId: this.order_table.id || null,
              orderCode: this.order_table.code || null,
              totalQty: this.order_table.total_qty || null,
              totalPrice: this.order_table.total_price || null,
              status: this.order_table.status || null,
              updatedAt: new Date().toISOString(),
            });
          } catch (e) {
            console.warn("[tables] mirror order to customer screen failed", e);
          }
        } else {
          this.order_table = {
            id: null,
            code: null,
            total_price: 0,
            total_qty: 0,
            status: "new",
            is_new_order: 1,
            table: {
              id: table.id,
              name: table.name,
              status: table.status,
              zone: table.zone,
            },
            history: [],
          };
          if (this.isBuffet) {
            this.defineStore.setBuffetPlanLines([]);
          }
        }
      } catch (error) {
        console.log("Error loading table details:", error);
        this.order_table = {
          id: null,
          code: null,
          total_price: 0,
          total_qty: 0,
          status: "new",
          is_new_order: 1,
          table: {
            id: table.id,
            name: table.name,
            status: table.status,
            zone: table.zone,
          },
          history: [],
        };
        if (this.isBuffet) {
          this.defineStore.setBuffetPlanLines([]);
        }
      } finally {
        this.loadingTableDetails = false;
      }
    },

    updateTableNewOrderStatus(tableId, newOrderStatus) {
      if (this.items.data) {
        const tableIndex = this.items.data.findIndex(
          (table) => table.id === tableId
        );
        if (tableIndex !== -1 && this.items.data[tableIndex].active_ticket) {
          this.items.data[tableIndex].active_ticket.is_new_order =
            newOrderStatus;
        }
      }

      if (this.draggableTables) {
        const draggableTableIndex = this.draggableTables.findIndex(
          (table) => table.id === tableId
        );
        if (
          draggableTableIndex !== -1 &&
          this.draggableTables[draggableTableIndex].active_ticket
        ) {
          this.draggableTables[draggableTableIndex].active_ticket.is_new_order =
            newOrderStatus;
        }
      }
    },

    handleScroll() {
      if (!this.$el || !this.$el.querySelector) return;
      const tableCardsContainer = this.$el.querySelector(
        ".table-cards-container"
      );
      if (!tableCardsContainer) return;

      const scrollPosition =
        tableCardsContainer.scrollTop + tableCardsContainer.clientHeight;
      const scrollHeight = tableCardsContainer.scrollHeight;

      if (
        scrollPosition >= scrollHeight - 100 &&
        !this.loading &&
        this.hasMore
      ) {
        this.loadMore();
      }
    },

    async loadMore() {
      if (this.loading || !this.hasMore) return;
      this.current_page++;
      await this.fetchItem(true);
    },

    tableStatusColor(status) {
      const colors = {
        available: "green",
        busy: "red",
        occupied: "red",
        reserved: "orange",
        cleaning: "blue",
        draft: "grey",
        completed: "success",
        pending: "orange",
        preparing: "blue",
        ready: "green",
      };
      return colors[status] || "grey";
    },

    getStatusColorByType(status, type = "item") {
      const colors = {
        table: {
          available: {
            color: "green",
            bgColor: "light-green",
            icon: "fal fa-check-circle",
          },
          busy: { color: "red", bgColor: "light-red", icon: "fal fa-users" },
          occupied: {
            color: "red",
            bgColor: "light-red",
            icon: "fal fa-users",
          },
          reserved: {
            color: "orange",
            bgColor: "light-orange",
            icon: "fal fa-clock",
          },
          cleaning: {
            color: "blue",
            bgColor: "light-blue",
            icon: "fal fa-broom",
          },
          draft: { color: "grey", bgColor: "light-grey", icon: "fal fa-edit" },
          completed: {
            color: "success",
            bgColor: "light-success",
            icon: "fal fa-check",
          },
        },
        order: {
          pending: {
            color: "orange",
            bgColor: "light-orange",
            icon: "fal fa-clock",
          },
          cooking: {
            color: "blue",
            bgColor: "light-blue",
            icon: "fal fa-utensils",
          },
          preparing: {
            color: "blue",
            bgColor: "light-blue",
            icon: "fal fa-blender",
          },
          ready: {
            color: "green",
            bgColor: "light-green",
            icon: "fal fa-check",
          },
          overdue: {
            color: "deep-orange",
            bgColor: "light-deep-orange",
            icon: "fal fa-exclamation-triangle",
          },
          cancel: {
            color: "red",
            bgColor: "light-red",
            icon: "fal fa-times-circle",
          },
          cancelled: {
            color: "red",
            bgColor: "light-red",
            icon: "fal fa-times-circle",
          },
          delivered: {
            color: "success",
            bgColor: "light-success",
            icon: "fal fa-truck",
          },
          done: {
            color: "success",
            bgColor: "light-success",
            icon: "fal fa-check-double",
          },
          new: {
            color: "primary",
            bgColor: "light-primary",
            icon: "fal fa-plus-circle",
          },
        },
        item: {
          pending: {
            color: "orange",
            bgColor: "light-orange",
            icon: "fal fa-hourglass-half",
          },
          cooking: {
            color: "blue",
            bgColor: "light-blue",
            icon: "fal fa-fire",
          },
          preparing: {
            color: "indigo",
            bgColor: "light-indigo",
            icon: "fal fa-blender",
          },
          ready: {
            color: "green",
            bgColor: "light-green",
            icon: "fal fa-check-circle",
          },
          overdue: {
            color: "deep-orange",
            bgColor: "light-deep-orange",
            icon: "fal fa-exclamation-circle",
          },
          cancel: {
            color: "red",
            bgColor: "light-red",
            icon: "fal fa-times-circle",
          },
          cancelled: {
            color: "red",
            bgColor: "light-red",
            icon: "fal fa-times-circle",
          },
          delivered: {
            color: "success",
            bgColor: "light-success",
            icon: "fal fa-check",
          },
          done: {
            color: "success",
            bgColor: "light-success",
            icon: "fal fa-check-double",
          },
        },
      };

      return (
        (colors[type] && colors[type][status]) || {
          color: "grey",
          bgColor: "light-grey",
          icon: "fal fa-question-circle",
        }
      );
    },

    getComputedStatusColor(status) {
      const statusInfo = this.getStatusColorByType(status, "item");
      const colorName = statusInfo.color;

      const colorMap = {
        primary: "#1976D2",
        green: "#4CAF50",
        orange: "#FF9800",
        "deep-orange": "#FF5722",
        amber: "#FFC107",
        blue: "#2196F3",
        indigo: "#3F51B5",
        success: "#4CAF50",
        red: "#F44336",
        grey: "#9E9E9E",
        "light-green": "#81C784",
        "light-orange": "#FFB74D",
        "light-blue": "#64B5F6",
        "light-indigo": "#7986CB",
        "light-red": "#E57373",
        "light-grey": "#BDBDBD",
      };

      return colorMap[colorName] || colorName || "#9E9E9E";
    },

    async printOrderToKitchen() {
      if (!this.order_table || !this.order_table.id) {
        toast(this, "No order selected", "error");
        return;
      }

      try {
        const confirmed = await confirm({
          props: this,
          title: this.$t("print_kitchen_order"),
          text: this.$t("print_kitchen_confirm", {
            table: this.order_table.table?.name || "N/A",
            order: this.order_table.code || "N/A",
          }),
          confirmText: this.$t("print_now"),
          cancelText: this.$t("cancel"),
        });

        if (!confirmed) return;

        this.isLoading = true;

        const defaultPrinter =
          this.$store.getters["printers/defaultKitchenPrinter"];
        // Avoid calling method-style module getters via store.getters["x/y"](...) —
        // some builds/runtime paths do not expose them as functions. Mirror
        // printers module getter `availablePrinters` using module state.
        const moduleState = this.$store.state.printers;
        const allPrinters = (moduleState && moduleState.printers) || [];
        const availablePrinters = allPrinters.filter(
          (printer) =>
            printer.connection_status === "online" &&
            Array.isArray(printer.print_categories) &&
            printer.print_categories.includes("kitchen")
        );

        if (!defaultPrinter && availablePrinters.length === 0) {
          toast(this, "no_kitchen_printer_available", "warning");
          return;
        }

        const orderData = {
          tableName: this.order_table.table?.name || "N/A",
          orderNumber: this.order_table.code || "N/A",
          staffName: this.user?.first_name || "N/A",
          restaurantName: this.user?.stores?.name || "Restaurant",
          items:
            this.order_table.history
              ?.filter((item) =>
                ["pending", "cooking", "preparing"].includes(item.status)
              )
              .map((item) => ({
                name: item.product_id?.name || "Unknown Item",
                quantity: item.qty || 1,
                notes: item.note || "",
                modifications: item.modifications || "",
                status: item.status,
              })) || [],
        };

        const printer = defaultPrinter || availablePrinters[0];

        const printerService = new PrinterService(this.$http);
        const printContent = printerService.generateKitchenOrderPrint(
          orderData,
          printer
        );

        const printJob = {
          type: "kitchen_order",
          content: printContent,
          options: {
            copies: 1,
            cut_paper: true,
          },
          order_data: orderData,
          order_id: this.order_table.id,
        };

        const result = await this.$store.dispatch("printers/sendPrintJob", {
          printerId: printer.id,
          printJob,
        });

        if (result.success) {
          toast(this, "kitchen_order_sent", "success");

          if (this.order_table.history) {
            this.order_table.history.forEach((item) => {
              if (["pending", "cooking", "preparing"].includes(item.status)) {
                this.$set(item, "kitchen_sent", true);
                this.$set(item, "kitchen_sent_at", new Date().toISOString());
              }
            });
          }

          this.showPrintStatusDialog(result);
        } else {
          throw new Error(result.message || "Print failed");
        }
      } catch (error) {
        console.error("Error printing to kitchen:", error);
        toast(this, "kitchen_print_error", "error");
      } finally {
        this.isLoading = false;
      }
    },

    showPrintStatusDialog(printResult) {
      this.$swal.fire({
        icon: "success",
        title: this.$t("print_sent"),
        text: this.$t("print_sent_success", {
          printer: printResult.printerName || "Kitchen Printer",
        }),
        confirmButtonText: this.$t("ok"),
        showCancelButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    },

    async mergeTable() {
      if (!this.selectedTable) {
        await toast(this, "no_table_selected", "error");
        return;
      }

      if (!this.order_table || !this.order_table.id) {
        await toast(this, "no_table_selected", "error");
        return;
      }

      // Close bottom sheet if it exists (mobile view) before opening merge modal
      if (this.showTableDetails !== undefined) {
        this.showTableDetails = false;
      }

      this.isMergeOperation = true;

      await this.fetchAllTablesForMove();

      this.selectedMoveTargetTable = null;
      this.moveOrderSearch = "";
      this.moveOrderStatusFilter = "";
      this.moveOrderZoneFilter = "";
      this.moveOrderModal = true;
    },

    applyDiscount() {
      if (this.order_table && this.order_table.id) {
        this.discountModal = true;
      } else {
        toast(this, "no_table_selected", "error");
      }
    },

    handleDiscountApply() {
      try {
        const discountData = this.$refs.discountModalRef?.getDiscountData();

        if (!discountData) {
          toast(this, "invalid_discount_value", "error");
          return;
        }

        this.appliedDiscount = discountData;

        this.discountModal = false;
        toast(this, "discount_applied_success", "success");

        this.$refs.discountModalRef?.reset();
      } catch (error) {
        console.error("Error applying discount:", error);
        toast(this, "error_applying_discount", "error");
      }
    },

    async moveOrder() {
      if (!this.order_table || !this.order_table.id) {
        toast(this, "no_table_selected", "error");
        return;
      }

      const selectedItems =
        this.order_table.history?.filter((item) => item.selected) || [];

      if (selectedItems.length === 0) {
        toast(this, "select_items_to_move", "warning");
        return;
      }

      if (this.zone_list.length === 0) {
        await this.fetchZones();
      }

      this.fetchAllTablesForMove();

      this.selectedMoveTargetTable = null;
      this.moveOrderSearch = "";
      this.moveOrderStatusFilter = "";
      this.moveOrderZoneFilter = "";
      this.moveOrderModal = true;
    },

    async fetchAllTablesForMove() {
      try {
        this.loadingTablesForMove = true;
        const params = `not_in=${this.selectedTable?.id}`;
        const { data } = await this.$http.get(`store_table?${params}`);

        if (data.status) {
          this.allTablesForMove = data.data;
        }
      } catch (error) {
        console.error("Error fetching all tables for move:", error);
        toast(this, "error_fetching_tables", "error");
      } finally {
        this.loadingTablesForMove = false;
      }
    },

    selectMoveTargetTable(tableId) {
      this.selectedMoveTargetTable = tableId;
    },

    async submitMoveOrder() {
      if (!this.selectedMoveTargetTable) {
        toast(this, "please_select_target_table", "warning");
        return;
      }

      const targetTable = this.allTablesForMove?.find(
        (t) => t.id === this.selectedMoveTargetTable
      );

      if (this.isMergeOperation) {
        const sourceTableName = this.selectedTable?.name || "Selected Table";
        const targetTableName = targetTable?.name || "Target Table";

        const confirmObject = {
          props: this,
          title: "are_you_sure",
          text: this.$t("confirm_merge_tables_message", {
            source: sourceTableName,
            target: targetTableName,
          }),
          confirmButtonText: "yes_merge",
          cancelButtonText: "cancel",
          icon: "warning",
        };

        const mergeConfirmed = await confirm(confirmObject);

        if (!mergeConfirmed || !mergeConfirmed.isConfirmed) {
          return;
        }

        try {
          const { data: targetData } = await this.$http.get(
            `get_table_order/${this.selectedMoveTargetTable}?check=0`
          );

          const body = {
            type: targetData.status && targetData.data ? "merge" : "move",
            from_table_id: this.selectedTable.id,
            from_ticket_id: this.order_table.id,
            to_table_id: this.selectedMoveTargetTable,
            to_ticket_id:
              targetData.status && targetData.data ? targetData.data.id : null,
          };

          const { data } = await this.$http.post("move_merge_table", body);

          if (data.status) {
            this.moveOrderModal = false;
            this.selectedMoveTargetTable = null;
            this.allSelected = false;
            await this.fetchItem();
            await this.fetchStatistics();

            this.selectedTable = null;
            this.order_table = {};

            await success(this, data.message);
          }
        } catch (error) {
          console.error("Error in merge operation:", error);
          toast(this, "error_merge_table", "error");
        }
        return;
      }

      try {
        this.isLoading = true;
        this.SET_OVERLAY(true);

        const selectedItems =
          this.order_table.history?.filter((item) => item.selected) || [];

        if (selectedItems.length === 0) {
          toast(this, "no_items_selected", "warning");
          this.isLoading = false;
          this.SET_OVERLAY(false);
          return;
        }

        // Store target table info before move
        const targetTable = this.allTablesForMove?.find(
          (t) => t.id === this.selectedMoveTargetTable
        );

        const moveData = {
          source_table_id: this.selectedTable.id,
          target_table_id: this.selectedMoveTargetTable,
          selected_items: selectedItems.map((item) => item.id),
        };

        const { data } = await this.$http.post("move_table_order", moveData);

        if (data.status) {
          this.moveOrderModal = false;
          this.selectedMoveTargetTable = null;
          this.allSelected = false;

          await toast(this, "move_order_success", "success");

          // Refresh source table details
          await this.loadTableDetails(this.selectedTable);

          // Refresh target table details to verify items appear
          if (targetTable) {
            await this.loadTableDetails(targetTable);
          }

          // Refresh table list and statistics
          await this.fetchItem();
          await this.fetchStatistics();

          // Clear selection
          this.selectedTable = null;
          this.order_table = {};

          // Close bottom sheet if it exists (mobile view)
          if (this.showTableDetails !== undefined) {
            this.showTableDetails = false;
          }

          // Clear overlay after all operations complete
          this.isLoading = false;
          this.SET_OVERLAY(false);
          this.$store.commit("index/SET_OVERLAY", false);
          this.$forceUpdate();
          // Double-check overlay is actually false
          this.$nextTick(() => {
            const overlayState = this.$store.getters["index/overlay"];
            if (this.overlay || overlayState) {
              this.SET_OVERLAY(false);
              this.$store.commit("index/SET_OVERLAY", false);
              this.$forceUpdate();
            }
          });
        } else {
          toast(this, data.message || "error_moving_order", "error");
          // Clear overlay on failure
          this.isLoading = false;
          this.SET_OVERLAY(false);
          this.$store.commit("index/SET_OVERLAY", false);
          this.$forceUpdate();
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || "error_moving_order";
        toast(this, errorMessage, "error");
        // Clear overlay on error
        this.isLoading = false;
        this.SET_OVERLAY(false);
        this.$store.commit("index/SET_OVERLAY", false);
        this.$forceUpdate();
      } finally {
        // Safety check - ensure overlay is cleared even if something went wrong
        this.$nextTick(() => {
          if (this.overlay) {
            this.SET_OVERLAY(false);
            this.$store.commit("index/SET_OVERLAY", false);
            this.$forceUpdate();
          }
          // Double-check after a delay
          setTimeout(() => {
            if (this.overlay) {
              this.SET_OVERLAY(false);
              this.$forceUpdate();
            }
          }, 200);
        });
      }
    },

    async checkout() {
      if (this.order_table && this.order_table.id) {
        await this.fetchActiveCurrencies(this);

        const manuallySelectedItems =
          this.order_table.history?.filter((item) => item.selected) || [];

        if (manuallySelectedItems.length > 0) {
          const validSelectedItems = manuallySelectedItems.filter(
            (item) => item.status !== "pending" && item.status !== "cancel"
          );

          if (validSelectedItems.length === 0) {
            toast(this, "no_valid_items_for_checkout", "warning");
            return;
          }

          const selectedTotal = validSelectedItems.reduce(
            (sum, item) => sum + (item.total_price || 0),
            0
          );

          this.selectedItemsForCheckout = validSelectedItems.map(
            (item) => item.id
          );
          this.checkBill("done", this.order_table.id, selectedTotal);
        } else {
          const allItems = this.order_table.history || [];
          const deliveredItems = allItems.filter(
            (item) => item.status === "delivered" && item.status !== "cancel"
          );

          if (
            deliveredItems.length > 0 &&
            deliveredItems.length <
              allItems.filter((i) => i.status !== "cancel").length
          ) {
            const deliveredTotal = deliveredItems.reduce(
              (sum, item) => sum + (item.total_price || 0),
              0
            );

            this.selectedItemsForCheckout = deliveredItems.map(
              (item) => item.id
            );
            this.checkBill("done", this.order_table.id, deliveredTotal);
          } else {
            this.selectedItemsForCheckout = null;
            this.checkBill(
              "done",
              this.order_table.id,
              this.order_table.total_price
            );
          }
        }
      }
    },

    navigateToBillReport() {
      this.$router.push({ name: "report_bill" });
    },

    navigateToProducts() {
      this.$router.push({ name: "products" });
    },

    /**
     * Laravel serializes the buffetPlans relation as buffet_plans in JSON.
     * Alias so templates and hydrateBuffetPiniaFromOrderTable use buffetPlans.
     */
    normalizeOrderTablePayload(raw) {
      if (!raw) return raw;
      if (raw.buffet_plans && !raw.buffetPlans) {
        this.$set(raw, "buffetPlans", raw.buffet_plans);
      }
      return raw;
    },

    mapApiBuffetPlansToLines(plans) {
      return (plans || []).map((p) => {
        let includedIds = [];
        if (Array.isArray(p.included_category_ids)) {
          includedIds = p.included_category_ids
            .map((id) => Number(id))
            .filter((id) => id > 0);
        } else if (Array.isArray(p.categories)) {
          includedIds = p.categories
            .map((c) => Number(c.id))
            .filter((id) => id > 0);
        }
        return {
          buffet_plan_id: p.buffet_plan_id,
          plan_name: p.plan_name,
          unit_price: Number(p.unit_price) || 0,
          persons: Math.max(1, Number(p.persons) || 1),
          subtotal: Number(p.subtotal) || 0,
          included_category_ids: includedIds,
        };
      });
    },

    hydrateBuffetPiniaFromOrderTable() {
      if (!this.isBuffet) {
        return;
      }
      if (!this.order_table || !this.order_table.id) {
        return;
      }
      const plans = this.order_table.buffetPlans;
      if (plans && plans.length) {
        this.defineStore.setBuffetPlanLines(
          this.mapApiBuffetPlansToLines(plans),
        );
      } else {
        this.defineStore.setBuffetPlanLines([]);
      }
    },

    openTableBuffet() {
      if (!this.selectedTable) {
        toast(this, "no_table_selected", "warning");
        return;
      }
      if (this.selectedTable.status !== "available") {
        toast(this, "table_not_available", "warning");
        return;
      }
      this.buffetSetupInitialLines = [];
      this.buffetSetupModal = true;
    },

    editBuffetPlanFromSidebar() {
      if (!this.order_table || !this.order_table.id) {
        toast(this, "buffet_no_open_ticket", "error");
        return;
      }
      const plans = this.order_table.buffetPlans || [];
      this.buffetSetupInitialLines = plans.length
        ? this.mapApiBuffetPlansToLines(plans).map((l) => ({
            buffet_plan_id:
              l.buffet_plan_id != null ? Number(l.buffet_plan_id) : null,
            plan_name: l.plan_name,
            unit_price: Number(l.unit_price) || 0,
            persons: Math.max(1, Number(l.persons) || 1),
            subtotal: Number(l.subtotal) || 0,
            included_category_ids: l.included_category_ids || [],
          }))
        : [];
      this.buffetSetupModal = true;
    },

    async openBuffetEditForTableCard(item) {
      await this.selectTable(item);
      this.editBuffetPlanFromSidebar();
    },

    /** After fetchItem(), sync selectedTable with refreshed items.data so status/busy flags update */
    refreshSelectedTableFromList() {
      if (!this.selectedTable) {
        return;
      }
      const id = this.selectedTable.id;
      const next =
        (this.items?.data || []).find((t) => t.id === id) ||
        (this.draggableTables || []).find((t) => t.id === id);
      if (next) {
        this.selectedTable = next;
      }
    },

    async submitBuffetSetup(lines) {
      const payload = (lines || []).map((l) => ({
        buffet_plan_id: Number(l.buffet_plan_id),
        persons: Math.max(1, Number(l.persons) || 1),
      }));
      if (!payload.length) {
        toast(this, "buffet_select_plan", "error");
        return;
      }
      if (!this.selectedTable) {
        toast(this, "no_table_selected", "warning");
        return;
      }
      this.isLoading = true;
      const requestBody = {
        table_id: this.selectedTable.id,
        buffet_plans: payload,
      };
      try {
        const { data } = await this.$http.post(
          "buffet_table_setup",
          requestBody,
        );
        if (data && data.status) {
          this.buffetSetupModal = false;
          await this.fetchItem();
          this.refreshSelectedTableFromList();
          await this.fetchStatistics();
          if (this.selectedTable) {
            await this.loadTableDetails(this.selectedTable);
          }
          await success(this, data.message || "success");
        } else {
          console.error(
            "[buffet_table_setup] server returned not-ok",
            requestBody,
            data,
          );
          await toast(this, data?.message || "error_occurred", "error");
        }
      } catch (e) {
        console.error("[buffet_table_setup] failed", {
          url: "buffet_table_setup",
          payload: requestBody,
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });
        await toast(this, "error_occurred", "error");
      } finally {
        this.isLoading = false;
      }
    },

    async closeTableBuffet() {
      if (!this.selectedTable || !(this.order_table && this.order_table.id)) {
        toast(this, "buffet_no_open_ticket", "error");
        return;
      }
      const { isConfirmed } = await confirm({
        props: this,
        title: "confirm",
        text: "buffet_close_table_confirm",
      });
      if (!isConfirmed) {
        return;
      }
      const closePayload = {
        table_id: this.selectedTable.id,
      };
      try {
        const { data } = await this.$http.post(
          "buffet_table_close",
          closePayload,
        );
        if (data && data.status) {
          this.defineStore.setBuffetPlanLines([]);
          this.order_table = {};
          this.buffetSetupModal = false;
          await this.fetchItem();
          this.refreshSelectedTableFromList();
          await this.fetchStatistics();
          await success(this, data.message || "success");
        } else {
          console.error(
            "[buffet_table_close] server returned not-ok",
            closePayload,
            data,
          );
          await toast(this, data?.message || "error_occurred", "error");
        }
      } catch (e) {
        console.error("[buffet_table_close] failed", {
          url: "buffet_table_close",
          payload: closePayload,
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });
        await toast(this, "error_occurred", "error");
      }
    },

    async updateTableStatus(status) {
      try {
        await this.$http.put(`store_table/${this.selectedTable.id}`, {
          status,
        });
        this.selectedTable.status = status;
        this.fetchItem();
        this.fetchStatistics();
      } catch (error) {
        console.log("error", error);
      }
    },

    executeAction(methodName) {
      if (this[methodName] && typeof this[methodName] === "function") {
        this[methodName]();
      } else {
        console.warn(`Method ${methodName} not found`);
      }
    },

    editItem(item) {
      this.update = true;
      this.form = item;
      this.dialogAdd = !this.dialogAdd;
    },

    async deleteItem({ id }) {
      try {
        const { isConfirmed } = await confirm({
          props: this,
          text: "want_to_delete",
        });
        if (isConfirmed) {
          const { data } = await this.$http.delete(`store_table/${id}`);
          if (data.status) {
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await success(this, data.message);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    viewItem(item) {
      this.form = item;
      this.dialog = !this.dialog;
    },

    async submitForm() {
      try {
        if (!this.form.name || this.form.name.trim() === "") {
          alert(this.$t("validation_required"));
          return;
        }
        // seats: whole number 1-20, default 4
        const seats = parseInt(this.form.seats, 10);
        this.form.seats = Number.isNaN(seats)
          ? 4
          : Math.min(20, Math.max(1, seats));

        if (this.update) {
          const { data } = await this.$http.put(
            `store_table/${this.form.id}`,
            this.form
          );
          if (data.status) {
            this.dialogAdd = !this.dialogAdd;
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await success(this, data.message);
          }
        } else {
          this.form.zone_id =
            typeof this.form.zone_id === "object" && this.form.zone_id !== null
              ? this.form.zone_id.id
              : this.form.zone_id;
          const { data } = await this.$http.post("store_table", this.form);
          if (data.status) {
            this.dialogAdd = !this.dialogAdd;
            await Help.clearForm(this.form, this);
            await this.fetchItem();
            await success(this, data.message);
          }
        }
      } catch (error) {
        console.log("error", error);
      }
    },

    openTable({ id }) {
      this.reservedModal = !this.reservedModal;
      this.table_id = id;
    },

    bookTable() {
      if (!this.selectedTable) {
        toast(this, "no_table_selected", "error");
        return;
      }

      if (this.selectedTable.status !== "available") {
        toast(this, "table_not_available", "warning");
        return;
      }

      this.table_id = this.selectedTable.id;
      this.note = "";
      this.status = "";
      this.reservedModal = true;
    },

    bookTableFromCard(table) {
      if (!table) {
        toast(this, "no_table_selected", "error");
        return;
      }

      if (table.status !== "available") {
        toast(this, "table_not_available", "warning");
        return;
      }

      this.table_id = table.id;
      this.note = "";
      this.status = "";
      this.reservedModal = true;
    },

    async reserved() {
      try {
        const body = {
          table_id: this.table_id,
          note: this.note,
        };
        const { data } = await this.$http.post("reserved", body);
        if (data.status) {
          await Help.clearForm(this.form, this);
          this.reservedModal = !this.reservedModal;
          this.note = "";
          await this.fetchItem();
          await success(this, data.message);
        }
      } catch (error) {
        console.log(error);
      }
    },

    async openPayment(val) {
      try {
        if (!val.ticket) return;
        if (val.ticket.is_new_order) {
          val.ticket.is_new_order = 0;
        }
        const { data } = await this.$http.get(`get_table_order/${val.id}`);
        this.inputValue = "";
        this.order_table = this.normalizeOrderTablePayload(data.data);
        this.billDetail = !this.billDetail;
      } catch (error) {
        console.log(error);
      }
    },

    async checkBill(status, ticket_id, total) {
      this.ticket_id = ticket_id;
      this.status = status;
      this.total_price = total;

      // Close bottom sheet if it exists (mobile view) before opening payment modal
      if (this.showTableDetails !== undefined) {
        this.showTableDetails = false;
      }

      if (status === "cancel") {
        this.billDetail = false;
        this.note = "";
        this.reservedModal = true;
      } else {
        this.billDetail = false;
        this.paymentModal = true;
        this.$nextTick(() => {
          const due = this.amountDue;
          this.total_price = due;
          this.inputValue = due.toString();
        });
      }
    },

    async handlePaymentSubmit(paymentDetails) {
      try {
        this.isLoading = true;

        this.inputValue = paymentDetails.paidAmount.toString();

        let paidAmountInLAK = paymentDetails.paidAmount;
        let exchangeRate = 1;

        const isForeignCurrency =
          paymentDetails.currency_code &&
          paymentDetails.currency_code !== "LAK";

        if (isForeignCurrency && paymentDetails.exchange_rate) {
          const paidAmount = parseFloat(paymentDetails.paidAmount);
          exchangeRate = parseFloat(paymentDetails.exchange_rate);
          paidAmountInLAK = paidAmount * exchangeRate;
          paidAmountInLAK = Math.round(paidAmountInLAK);
        }

        let calculatedAmountToPay = paymentDetails.paidAmount;

        const amountDueLak = this.amountDue;

        if (isForeignCurrency && paymentDetails.exchange_rate) {
          exchangeRate = parseFloat(paymentDetails.exchange_rate);
          calculatedAmountToPay = amountDueLak / exchangeRate;

          paidAmountInLAK = calculatedAmountToPay * exchangeRate;
        }

        const updatedPaymentDetails = {
          ...paymentDetails,
          paidAmount: calculatedAmountToPay,
          totalAmount: amountDueLak,
          change: Math.max(0, paidAmountInLAK - amountDueLak),
        };

        const body = {
          status: this.status || "done",
          ticket_id: this.ticket_id,
          note: this.note || "",
          paid: calculatedAmountToPay,
          payment_method: paymentDetails.method,
          payment_details: updatedPaymentDetails,
          selected_items: this.selectedItemsForCheckout || null,
          discount: this.appliedDiscount
            ? {
                type: this.appliedDiscount.type,
                value: this.appliedDiscount.value,
                amount: this.appliedDiscount.amount,
                selected_items: this.appliedDiscount.selected_items,
              }
            : null,
        };

        if (paymentDetails.method === "cash_transfer") {
          body.cash_amount = parseFloat(paymentDetails.cashAmount);
          body.transfer_amount = parseFloat(paymentDetails.transferAmount);
        }

        let tolerance;
        if (isForeignCurrency && paymentDetails.exchange_rate) {
          tolerance = Math.max(amountDueLak * 0.005, 50);
        } else {
          tolerance = 5;
        }

        const isPaymentSufficient =
          paidAmountInLAK + tolerance >= amountDueLak;
        const difference = paidAmountInLAK - amountDueLak;

        let status = false;
        let total = amountDueLak;
        let message = "";

        if (isPaymentSufficient) {
          status = true;
          const changeAmount = paidAmountInLAK - amountDueLak;
          if (changeAmount > 0) {
            const changeInOriginalCurrency = isForeignCurrency
              ? (changeAmount / exchangeRate).toFixed(2)
              : changeAmount;
            message = `ກວດສອບຈຳນວນຂໍ້ມູນຂອງທ່ານ ${Help.moneyFormat(
              changeInOriginalCurrency
            )}`;
          } else {
            message = this.$t("full_payment");
          }
        } else {
          const remainingAmount = amountDueLak - paidAmountInLAK;
          message = `ກວດສອບຈຳນວນຂໍ້ມູນຂອງທ່ານ ${Help.moneyFormat(
            remainingAmount
          )}`;
        }

        if (this.status === "done" && !isPaymentSufficient) {
          toast(this, "insufficient_payment", "error");
          return;
        }

        const { data } = await this.$http.post("check_out", body);

        if (data.status) {
          const isQRActive =
            this.user &&
            this.user.stores &&
            this.user.stores.is_active_qr === 1;

          if (
            paymentDetails.method === "qr" &&
            isQRActive &&
            data.data &&
            data.data.qr_code
          ) {
            console.log("📱 [tables] QR payment detected, generating QR code", {
              qr_code: data.data.qr_code,
              ticket_id: data.data.ticket_id || data.data.code,
            });

            try {
              const qrCode = await QRCode.toDataURL(data.data.qr_code);
              this.qrCodeData = {
                qrCode,
                ticket_id: data.data.ticket_id || data.data.code,
                amount: data.data.amount || data.data.total_price,
                terminal_label:
                  (data.data.terminal_label || "") +
                  (data.data.amount || data.data.total_price
                    ? " - " + (data.data.amount || data.data.total_price)
                    : ""),
              };

              console.log("✅ [tables] QR code generated", {
                ticket_id: this.qrCodeData.ticket_id,
                amount: this.qrCodeData.amount,
              });

              if (this.$refs.paymentPad) {
                this.$refs.paymentPad.setQRData(this.qrCodeData);
                console.log("✅ [tables] QR data set on UnifiedPaymentPad");
              }

              const ticketId =
                data.data.ticket_id || data.data.code || this.ticket_id;
              if (ticketId) {
                await this.listenTicket(ticketId);
                console.log("📡 [tables] Listening for QR payment success", {
                  ticket_id: ticketId,
                });
              }

              return;
            } catch (error) {
              console.error("❌ [tables] Error generating QR code:", error);
              toast(this, "error_generating_qr", "error");
              return;
            }
          }

          this.paymentModal = false;
          this.inputValue = "";
          this.note = "";
          this.ticket_id = "";
          this.status = "";
          this.reservedModal = false;
          this.billDetail = false;

          // Check if this was a partial checkout
          const wasPartialCheckout =
            body.selected_items && body.selected_items.length > 0;
          const originalTicketId =
            this.ticket_id || (this.order_table && this.order_table.id);

          this.selectedItemsForCheckout = null;
          this.allSelected = false;
          await Help.clearForm(this.form, this);

          // For partial checkout, we need to reload the original ticket to see remaining items
          // For full checkout, use the response data
          if (wasPartialCheckout && this.selectedTable) {
            // Partial checkout: reload table details to get original ticket with remaining items
            const tableId = this.selectedTable.id;
            await this.loadTableDetails(this.selectedTable);

            // Check if original ticket still exists with remaining items
            // get_table_order returns null if ticket is cancelled/done, so order_table.id will be null
            if (
              this.order_table &&
              this.order_table.id &&
              this.order_table.history &&
              this.order_table.history.length > 0
            ) {
              // Original ticket has remaining items - update table card
              this.total_price = this.order_table.total_price || 0;
              this.updateTableCardFromTicket(this.order_table);
            } else {
              // Original ticket was cancelled (all items checked out) - clear table card
              this.clearTableCard(tableId);
              this.selectedTable = null;
              this.order_table = {};
              this.total_price = 0;
              console.log(
                "All items checked out, original ticket cancelled, clearing table card"
              );
            }
          } else if (data.data && data.data.table_id) {
            // Full checkout: use API response data
            const tableId =
              typeof data.data.table_id === "object"
                ? data.data.table_id.id
                : data.data.table_id;

            // Check if ticket is done - done tickets don't show on table, so clear the card
            if (data.data.status === "done") {
              // Ticket is done - clear table card (done tickets don't appear on table)
              this.clearTableCard(tableId);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.selectedTable = null;
                this.order_table = {};
                this.total_price = 0;
                console.log(
                  "Table checkout complete, resetting selected table"
                );
              }
            } else if (
              data.data.status === "cancel" &&
              (!data.data.history || data.data.history.length === 0)
            ) {
              // Ticket is cancelled and empty - clear table card
              this.clearTableCard(tableId);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.selectedTable = null;
                this.order_table = {};
                this.total_price = 0;
                console.log(
                  "Table is cancelled and empty, resetting selected table"
                );
              }
            } else {
              // Ticket still has items or is not done - update table card
              this.updateTableCardFromTicket(data.data);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.order_table = data.data;
                if (this.order_table.history) {
                  this.order_table.history.forEach((item) => {
                    this.$set(item, "selected", false);
                  });
                }
                this.total_price = this.order_table.total_price || 0;
              }
            }
          } else {
            // Fallback: reload table details if response doesn't have table_id
            if (this.selectedTable) {
              await this.loadTableDetails(this.selectedTable);
              if (this.order_table && this.order_table.total_price) {
                this.total_price = this.order_table.total_price;
              }
            }
          }

          await this.fetchStatistics();

          const result = await successRestaurantCheckout(this, data.data);

          if (shouldPrintFromPaymentAlert(result)) {
            const object = await Help.mergeProduct(data.data);
            this.printBill = object;
            setTimeout(() => {
              this.$htmlToPaper("printBill");
            }, 500);
          }
        }
      } catch (error) {
        console.error("Payment processing error:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async handleManualPaymentSuccess(data) {
      console.log("✅ [tables] handleManualPaymentSuccess called", { data });

      try {
        const result = await successRestaurantCheckout(this, data);

        this.paymentModal = false;
        this.inputValue = "";
        this.note = "";
        this.ticket_id = "";
        this.status = "";
        this.reservedModal = false;
        this.billDetail = false;
        this.qrCodeData = null;
        this.selectedItemsForCheckout = null;
        this.allSelected = false;

        await Help.clearForm(this.form, this);

        // Use API response to update table card in-place instead of full refresh
        if (data && data.table_id) {
          const tableId =
            typeof data.table_id === "object"
              ? data.table_id.id
              : data.table_id;

          // Check if ticket is done - done tickets don't show on table
          if (data.status === "done") {
            // Ticket is done - clear table card
            this.clearTableCard(tableId);

            // Update order_table if this table is currently selected
            if (this.selectedTable && this.selectedTable.id === tableId) {
              this.selectedTable = null;
              this.order_table = {};
              this.total_price = 0;
            }
          } else if (
            data.status === "cancel" &&
            (!data.history || data.history.length === 0)
          ) {
            // Ticket is cancelled and empty - clear table card
            this.clearTableCard(tableId);

            // Update order_table if this table is currently selected
            if (this.selectedTable && this.selectedTable.id === tableId) {
              this.selectedTable = null;
              this.order_table = {};
              this.total_price = 0;
            }
          } else {
            // Ticket still has items - update table card
            this.updateTableCardFromTicket(data);

            // Update order_table if this table is currently selected
            if (this.selectedTable && this.selectedTable.id === tableId) {
              this.order_table = data;
              if (this.order_table.history) {
                this.order_table.history.forEach((item) => {
                  this.$set(item, "selected", false);
                });
              }
              this.total_price = this.order_table.total_price || 0;
            }
          }
        } else {
          // Fallback: reload table details if response doesn't have table_id
          if (this.selectedTable) {
            await this.loadTableDetails(this.selectedTable);
            if (this.order_table && this.order_table.total_price) {
              this.total_price = this.order_table.total_price;
            }
          }
        }

        await this.fetchStatistics();

        // Close bottom sheet if it exists (mobile view)
        if (this.showTableDetails !== undefined) {
          this.showTableDetails = false;
        }

        if (data && shouldPrintFromPaymentAlert(result)) {
          const object = await Help.mergeProduct(data);
          this.printBill = object;
          if (this.$el && this.$el.parentNode) {
            setTimeout(() => {
              if (this.$el && this.$el.parentNode) {
                this.$htmlToPaper("printBill");
              }
            }, 500);
          }
        }
      } catch (error) {
        console.error("Error handling manual payment success:", error);
        toast(this, "error_processing_payment", "error");
      }
    },

    handlePaymentAmountUpdated(data) {
      console.log("📊 [tables] handlePaymentAmountUpdated called", {
        data: data,
        currentInputValue: this.inputValue,
      });

      if (data && data.amount) {
        console.log(
          "💰 [tables] Updating inputValue from payment amount update",
          {
            oldAmount: this.inputValue,
            newAmount: data.amount,
            formattedNewAmount: data.formattedAmount,
            currency: data.currency?.code,
          }
        );

        this.inputValue = data.amount.toString();

        console.log(
          "✅ [tables] inputValue synchronized with UnifiedPaymentPad"
        );
      }
    },

    handleSwitchToCash() {
      console.log("💵 [tables] handleSwitchToCash called");

      if (this.$refs.paymentPad) {
        this.$refs.paymentPad.resetQR();
      }

      this.qrCodeData = null;
      this.currentTicketId = null;

      this.defineStore.setCurrency(null);

      console.log("✅ [tables] Switched to cash payment, QR state reset");
    },

    openNoted() {
      console.log("📝 [tables] openNoted called");
      this.actionHandler = "note";
    },

    async openExchangeRate() {
      console.log("💱 [tables] openExchangeRate called");
      console.log("🔍 [tables] Current state before opening exchange rate", {
        total_price: this.total_price,
        currentActionHandler: this.actionHandler,
        activeCurrencies: this.activeCurrencies,
        storeCurrency: this.defineStore.currency,
      });
      await this.fetchActiveCurrencies(this);
      this.actionHandler = "exchangeRate";
      console.log(
        "✅ [tables] Exchange rate dialog opened, actionHandler set to:",
        this.actionHandler
      );
    },

    openCreateCustomerDialog() {
      console.log("👤 [tables] openCreateCustomerDialog called");
      this.actionHandler = "customer";
    },

    submitExchangeRate(amount) {
      console.log(
        "💱 EXCHANGE RATE: Submitting",
        amount,
        this.defineStore.currency?.code
      );

      this.pendingConvertedAmount = amount;
      this.pendingCurrencyData = this.defineStore.currency;

      console.log("💾 [tables] Storing pending conversion data", {
        pendingConvertedAmount: this.pendingConvertedAmount,
        pendingCurrencyData: this.pendingCurrencyData,
      });

      this.actionHandler = "payment";

      const newInputValue = amount.toString();
      this.inputValue = newInputValue;

      console.log(
        "💱 EXCHANGE RATE: Updated to",
        amount,
        this.pendingCurrencyData?.code
      );
      console.log("🎯 [tables] Switching back to payment view");
      console.log("✅ [tables] submitExchangeRate completed");
    },

    trySetExternalCurrency(currency, retryCount) {
      const maxRetries = 5;
      const retryDelay = 100;

      if (this.$refs.paymentPad && this.$refs.paymentPad.setExternalCurrency) {
        console.log(
          "📞 [tables] Calling setExternalCurrency on UnifiedPaymentPad"
        );
        this.$refs.paymentPad.setExternalCurrency(currency);

        this.pendingCurrencyData = null;
        console.log(
          "✅ [tables] External currency set successfully, pendingCurrencyData cleared"
        );
      } else if (retryCount < maxRetries) {
        console.warn(
          `⚠️ [tables] paymentPad reference not available, retry ${
            retryCount + 1
          }/${maxRetries}`
        );
        setTimeout(() => {
          this.trySetExternalCurrency(currency, retryCount + 1);
        }, retryDelay);
      } else {
        console.error(
          "❌ [tables] Failed to set external currency after maximum retries"
        );
      }
    },

    trySetExternalPaymentAmount(amount, retryCount) {
      const maxRetries = 5;
      const retryDelay = 100;

      if (
        this.$refs.paymentPad &&
        this.$refs.paymentPad.setExternalPaymentAmount
      ) {
        console.log(
          "📞 [tables] Calling setExternalPaymentAmount on UnifiedPaymentPad"
        );
        this.$refs.paymentPad.setExternalPaymentAmount(amount);

        this.pendingConvertedAmount = null;
        console.log(
          "✅ [tables] External payment amount set successfully, pendingConvertedAmount cleared"
        );
      } else if (retryCount < maxRetries) {
        console.warn(
          `⚠️ [tables] paymentPad reference not available for payment amount, retry ${
            retryCount + 1
          }/${maxRetries}`
        );
        setTimeout(() => {
          this.trySetExternalPaymentAmount(amount, retryCount + 1);
        }, retryDelay);
      } else {
        console.error(
          "❌ [tables] Failed to set external payment amount after maximum retries"
        );
      }
    },

    async listenTicket(ticket_id) {
      console.log("📡 [tables] listenTicket called", { ticket_id });

      if (this._ticketPubNubListener) {
        try {
          this.pubnub.removeListener(this._ticketPubNubListener);
        } catch (error) {
          console.warn("Error removing previous ticket PubNub listener:", error);
        }
        this._ticketPubNubListener = null;
      }

      if (this.currentTicketId) {
        const previousChannel = String(this.currentTicketId);
        try {
          this.pubnub.unsubscribe({ channels: [previousChannel] });
        } catch (error) {
          console.warn("Error unsubscribing from previous channel:", error);
        }
      }

      this.currentTicketId = ticket_id;

      const channel = String(ticket_id);
      this._ticketPubNubListener = {
        message: (event) => {
          if (event.channel !== channel) {
            return;
          }
          if (event.message === "qr_payment_success") {
            console.log("✅ [tables] QR payment success received", {
              ticket_id,
              event,
            });
            this.handleQRPaymentSuccess(ticket_id);
          }
        },
      };
      this.pubnub.addListener(this._ticketPubNubListener);

      this.pubnub.subscribe({ channels: [channel] });
      console.log("📡 [tables] Subscribed to channel:", channel);
    },

    async handleQRPaymentSuccess(ticket_id) {
      console.log("✅ [tables] handleQRPaymentSuccess called", { ticket_id });

      try {
        const { data } = await this.$http.get(`bill_detail/${ticket_id}`);
        if (!data || !data.status) {
          console.error("Failed to fetch bill details", data);
          return;
        }

        this.printBill = data.data;

        this.paymentModal = false;
        this.inputValue = "";
        this.note = "";
        this.ticket_id = "";
        this.status = "";
        this.reservedModal = false;
        this.billDetail = false;
        this.qrCodeData = null;
        this.currentTicketId = null;
        this.selectedItemsForCheckout = null;
        this.allSelected = false;

        await Help.clearForm(this.form, this);

        // Use API response to update table card in-place instead of full refresh
        if (data.data && data.data.table_id) {
          this.updateTableCardFromTicket(data.data);

          // Update order_table if this table is currently selected
          if (
            this.selectedTable &&
            this.selectedTable.id ===
              (typeof data.data.table_id === "object"
                ? data.data.table_id.id
                : data.data.table_id)
          ) {
            // Check if ticket is cancelled or has no items
            if (
              data.data.status === "cancel" ||
              !data.data.history ||
              data.data.history.length === 0
            ) {
              // Table is now empty, reset
              this.selectedTable = null;
              this.order_table = {};
              this.total_price = 0;
            } else {
              // Update order_table with response data
              this.order_table = data.data;
              if (this.order_table.history) {
                this.order_table.history.forEach((item) => {
                  this.$set(item, "selected", false);
                });
              }
              this.total_price = this.order_table.total_price || 0;
            }
          }
        } else {
          // Fallback: reload table details if response doesn't have table_id
          if (this.selectedTable) {
            await this.loadTableDetails(this.selectedTable);
            if (this.order_table && this.order_table.total_price) {
              this.total_price = this.order_table.total_price;
            }
          }
        }

        await this.fetchStatistics();

        // Close bottom sheet if it exists (mobile view)
        if (this.showTableDetails !== undefined) {
          this.showTableDetails = false;
        }

        const alertResult = await successRestaurantCheckout(
          this,
          data.data,
          true
        );

        if (shouldPrintFromPaymentAlert(alertResult) && data.data) {
          const object = await Help.mergeProduct(data.data);
          this.printBill = object;
          if (this.$el && this.$el.parentNode) {
            setTimeout(() => {
              if (this.$el && this.$el.parentNode) {
                this.$htmlToPaper("printBill");
              }
            }, 500);
          }
        }
      } catch (error) {
        console.error("Error handling QR payment success:", error);
        toast(this, "error_processing_payment", "error");
      }
    },

    async makePayment() {
      try {
        if (this.status === "cancel" && !this.note) {
          return toast(this, "note_required", "error");
        }
        const body = {
          status: this.status,
          ticket_id: this.ticket_id,
          note: this.note,
          paid: this.inputValue,
          payment_method: "cash",
        };
        const { status, total, message } = await Help.comparePrice(
          this,
          this.inputValue,
          this.total_price
        );
        if (this.status === "done" && !status) {
          return toast(this, "insufficient_payment", "error");
        }
        const { data } = await this.$http.post("check_out", body);
        if (data.status) {
          this.paymentModal = false;
          this.inputValue = "";
          this.note = "";
          this.ticket_id = "";
          this.status = "";
          this.reservedModal = false;
          this.billDetail = false;
          this.allSelected = false;
          await Help.clearForm(this.form, this);

          // Use API response to update table card in-place instead of full refresh
          if (data.data && data.data.table_id) {
            const tableId =
              typeof data.data.table_id === "object"
                ? data.data.table_id.id
                : data.data.table_id;

            // Check if ticket is done - done tickets don't show on table
            if (data.data.status === "done") {
              // Ticket is done - clear table card
              this.clearTableCard(tableId);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.selectedTable = null;
                this.order_table = {};
                this.total_price = 0;
                console.log(
                  "Table checkout complete, resetting selected table"
                );
              }
            } else if (
              data.data.status === "cancel" &&
              (!data.data.history || data.data.history.length === 0)
            ) {
              // Ticket is cancelled and empty - clear table card
              this.clearTableCard(tableId);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.selectedTable = null;
                this.order_table = {};
                this.total_price = 0;
                console.log(
                  "Table is cancelled and empty, resetting selected table"
                );
              }
            } else {
              // Ticket still has items - update table card
              this.updateTableCardFromTicket(data.data);

              // Update order_table if this table is currently selected
              if (this.selectedTable && this.selectedTable.id === tableId) {
                this.order_table = data.data;
                if (this.order_table.history) {
                  this.order_table.history.forEach((item) => {
                    this.$set(item, "selected", false);
                  });
                }
                this.total_price = this.order_table.total_price || 0;
              }
            }
          } else {
            // Fallback: reload table details if response doesn't have table_id
            if (this.selectedTable) {
              await this.loadTableDetails(this.selectedTable);
              if (this.order_table && this.order_table.total_price) {
                this.total_price = this.order_table.total_price;
              }
            }
          }

          await this.fetchStatistics();

          const mst_tran = this.$t("order_cancel_success");
          const mgs =
            data.data.status === "done" || data.data.status === "delivered"
              ? message
              : mst_tran;
          const result = await successRestaurantCheckout(this, data.data);
          if (shouldPrintFromPaymentAlert(result)) {
            const object = await Help.mergeProduct(data.data);
            this.printBill = object;
            setTimeout(() => {
              this.$htmlToPaper("printBill");
            }, 500);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    generateQR(val) {
      this.form = val;
      const code = this.user.stores.code;
      // branch_id may arrive as a populated object {id, name} from the API —
      // extract the numeric id so the URL doesn't contain "[object Object]".
      const rawBranchId = val.branch_id;
      const branch_id =
        rawBranchId && typeof rawBranchId === "object"
          ? rawBranchId.id
          : rawBranchId || this.user?.branch_id?.id || "1";
      const params = `${code}/${branch_id}/${val.id}`;
      const qr = this.StringToQRCode(params);
      this.qr = qr;
      this.show_qr = !this.show_qr;
    },

    async onConfirm(id, status, total) {
      try {
        if (status === "cancel") {
          this.status = status;
          this.ticket_id = id;
          this.total_price = total;
          this.inputValue = total;
          this.billDetail = false;
          this.note = "";
          this.reservedModal = true;
          return;
        }
        const { isConfirmed } = await confirm({ props: this });
        if (isConfirmed) {
          await this.updateStatusBill(id, status);
        }
      } catch (error) {
        console.log(error);
      }
    },

    async updateStatusBill(id, status) {
      try {
        const body = { status };
        const { data } = await this.$http.put(`update_status_bill/${id}`, body);
        if (data.status) {
          this.order_table = data.data;
          await this.fetchItem();
          await success(this, "update_status_success");
        }
      } catch (error) {
        console.log(error);
      }
    },

    async updateStatusItem(val, status) {
      if (val.status === status) {
        toast(this, "items_already_in_status", "warning");
        return;
      }

      try {
        const body = { status };
        const { data } = await this.$http.put(
          `update_status_item/${val.id}`,
          body
        );
        if (data.status) {
          this.$set(val, "status", status);
          await this.fetchItem();
          this.order_table = data.data;
          toast(this, data.message, "success");
        }
      } catch (error) {
        console.log(error);
      }
    },

    statusTable(object) {
      if (Object.keys(object).length > 0 && object.ticket) {
        return object.ticket.is_new_order;
      }
      return false;
    },

    async fetchTable(item) {
      this.activeTable = item;
      const body = {
        url: `store_table?not_in=${item.table_id}`,
        that: this,
        commit: "SET_TABLES",
      };
      await this.fetch(body);
      this.billDetail = false;
      this.dialogMoveMerge = true;
    },

    async moveMergeTable() {
      try {
        const body = {
          type: this.type,
          from_table_id: this.activeTable.table_id,
          from_ticket_id: this.activeTable.id,
          to_table_id: this.table_id,
          to_ticket_id: this.order_table?.id || null,
        };
        const { data } = await this.$http.post("move_merge_table", body);
        if (data.status) {
          this.dialogMoveMerge = false;
          this.allSelected = false;
          await this.fetchItem();
          await this.fetchStatistics();

          this.selectedTable = null;
          this.order_table = {};

          await success(this, data.message);
        }
      } catch (error) {
        console.error("Error in moveMergeTable:", error);
        toast(this, "error_move_merge_table", "error");
      }
    },

    async setTable(id) {
      try {
        this.table_id = id;
        const { data } = await this.$http.get(`get_table_order/${id}?check=0`);
        if (data.status) {
          if (data.data) {
            this.type = "merge";
            this.order_table = this.normalizeOrderTablePayload(data.data);
            this.hydrateBuffetPiniaFromOrderTable();
          } else {
            this.type = "move";
          }
        }
      } catch (error) {
        console.error("Error in setTable:", error);
        toast(this, "error_fetching_table", "error");
      }
    },

    getStatusColor(status) {
      const colors = {
        pending: "orange",
        cooking: "blue",
        preparing: "blue",
        ready: "green",
        delivered: "success",
        done: "success",
        cancel: "error",
        cancelled: "error",
        overdue: "deep-orange",
        new: "primary",
        processing: "blue",
        completed: "success",
        failed: "error",
        pending_payment: "amber",
        paid: "success",
      };
      return colors[status] || "grey";
    },

    cancelSelectedItems() {
      const selectedItems = this.order_table.history.filter(
        (item) => item.selected
      );
      if (selectedItems.length === 0) return;

      confirm({
        props: this,
        title: "confirm_cancel_items",
        text: "confirm_cancel_items_message",
      }).then((result) => {
        if (result.isConfirmed) {
          this.processItemStatusUpdate(selectedItems, "cancel");
        }
      });
    },

    serveSelectedItems() {
      const selectedItems = this.order_table.history.filter(
        (item) => item.selected
      );
      if (selectedItems.length === 0) return;

      confirm({
        props: this,
        title: "confirm_serve_items",
        text: "confirm_serve_items_message",
      }).then((result) => {
        if (result.isConfirmed) {
          this.processItemStatusUpdate(selectedItems, "delivered");
        }
      });
    },

    async processItemStatusUpdate(items, status) {
      try {
        const itemIds = items.map((item) => item.id);
        const object = {
          ticket_id: this.order_table.id,
          item_ids: itemIds,
          status: status,
        };
        const { data } = await this.$http.put(
          "/update_multiple_order_items",
          object
        );

        if (data.status) {
          // Use API response data to update local state instead of re-fetching
          if (data.data) {
            // Update order_table with response data
            this.order_table = data.data;

            // Ensure selected flags are reset
            if (this.order_table.history) {
              this.order_table.history.forEach((item) => {
                this.$set(item, "selected", false);
              });
            }

            // Update table card in items.data and draggableTables
            this.updateTableCardFromTicket(data.data);
          } else {
            // data.data is null - means ticket was cancelled and table is now available
            // This happens when all items are cancelled
            let tableId = null;

            // Get table_id from selectedTable or order_table
            if (this.selectedTable) {
              tableId = this.selectedTable.id;
            } else if (this.order_table && this.order_table.table_id) {
              tableId =
                typeof this.order_table.table_id === "object"
                  ? this.order_table.table_id.id
                  : this.order_table.table_id;
            }

            if (tableId) {
              // Clear the order_table
              this.order_table = {};

              // Update table card to remove active_ticket and set status to available
              this.clearTableCard(tableId);

              // Reset selected table
              this.selectedTable = null;
            } else if (this.selectedTable) {
              // Fallback: reload table details if we don't have table_id
              await this.loadTableDetails(this.selectedTable);
            }
          }

          this.allSelected = false;
          toast(this, data.message, "success");
        } else {
          toast(this, data.message || this.$t("error_updating_items"), "error");
        }
      } catch (error) {
        console.error("Error updating item status:", error);
        toast(this, "error_updating_items", "error");
      }
    },

    clearTableCard(tableId) {
      // Remove active_ticket and set table status to available
      if (this.items.data) {
        const tableIndex = this.items.data.findIndex(
          (table) => table.id === tableId
        );
        if (tableIndex !== -1) {
          this.$set(this.items.data[tableIndex], "active_ticket", null);
          this.$set(this.items.data[tableIndex], "status", "available");
        }
      }

      if (this.draggableTables) {
        const draggableTableIndex = this.draggableTables.findIndex(
          (table) => table.id === tableId
        );
        if (draggableTableIndex !== -1) {
          this.$set(
            this.draggableTables[draggableTableIndex],
            "active_ticket",
            null
          );
          this.$set(
            this.draggableTables[draggableTableIndex],
            "status",
            "available"
          );
        }
      }
    },

    updateTableCardFromTicket(ticketData) {
      if (!ticketData || !ticketData.table_id) return;

      const tableId =
        typeof ticketData.table_id === "object"
          ? ticketData.table_id.id
          : ticketData.table_id;

      // Update in items.data
      if (this.items.data) {
        const tableIndex = this.items.data.findIndex(
          (table) => table.id === tableId
        );
        if (tableIndex !== -1) {
          // Update active_ticket with new totals and status
          if (this.items.data[tableIndex].active_ticket) {
            this.$set(
              this.items.data[tableIndex].active_ticket,
              "total_price",
              ticketData.total_price || 0
            );
            this.$set(
              this.items.data[tableIndex].active_ticket,
              "total_qty",
              ticketData.total_qty || 0
            );
            this.$set(
              this.items.data[tableIndex].active_ticket,
              "status",
              ticketData.status
            );

            // If ticket is cancelled or has no items, remove active_ticket
            if (
              ticketData.status === "cancel" ||
              !ticketData.history ||
              ticketData.history.length === 0
            ) {
              this.$set(this.items.data[tableIndex], "active_ticket", null);
            }
          } else if (
            ticketData.status !== "cancel" &&
            ticketData.history &&
            ticketData.history.length > 0
          ) {
            // Create active_ticket if it doesn't exist and ticket has items
            this.$set(this.items.data[tableIndex], "active_ticket", {
              id: ticketData.id,
              code: ticketData.code,
              total_price: ticketData.total_price || 0,
              total_qty: ticketData.total_qty || 0,
              status: ticketData.status,
            });
          }
        }
      }

      // Update in draggableTables
      if (this.draggableTables) {
        const draggableTableIndex = this.draggableTables.findIndex(
          (table) => table.id === tableId
        );
        if (draggableTableIndex !== -1) {
          // Update active_ticket with new totals and status
          if (this.draggableTables[draggableTableIndex].active_ticket) {
            this.$set(
              this.draggableTables[draggableTableIndex].active_ticket,
              "total_price",
              ticketData.total_price || 0
            );
            this.$set(
              this.draggableTables[draggableTableIndex].active_ticket,
              "total_qty",
              ticketData.total_qty || 0
            );
            this.$set(
              this.draggableTables[draggableTableIndex].active_ticket,
              "status",
              ticketData.status
            );

            // If ticket is cancelled or has no items, remove active_ticket
            if (
              ticketData.status === "cancel" ||
              !ticketData.history ||
              ticketData.history.length === 0
            ) {
              this.$set(
                this.draggableTables[draggableTableIndex],
                "active_ticket",
                null
              );
            }
          } else if (
            ticketData.status !== "cancel" &&
            ticketData.history &&
            ticketData.history.length > 0
          ) {
            // Create active_ticket if it doesn't exist and ticket has items
            this.$set(
              this.draggableTables[draggableTableIndex],
              "active_ticket",
              {
                id: ticketData.id,
                code: ticketData.code,
                total_price: ticketData.total_price || 0,
                total_qty: ticketData.total_qty || 0,
                status: ticketData.status,
              }
            );
          }
        }
      }
    },

    async printSelectedItems() {
      const selectedItems = this.order_table.history.filter(
        (item) => item.selected
      );
      if (selectedItems.length === 0) return;

      try {
        const printData = {
          ...this.order_table,
          history: selectedItems,
          table_id: this.selectedTable
            ? {
                id: this.selectedTable.id,
                name: this.selectedTable.name,
              }
            : this.order_table.table_id || this.order_table.table,
          user_id: this.user
            ? {
                id: this.user.id,
                first_name:
                  this.user.first_name || this.user.name || this.user.username,
              }
            : this.order_table.user_id,
          total_price: selectedItems.reduce(
            (sum, item) => sum + (item.total_price || 0),
            0
          ),
          total_qty: selectedItems.reduce(
            (sum, item) => sum + (item.qty || 0),
            0
          ),
        };

        this.printBill = printData;

        setTimeout(() => {
          this.$htmlToPaper("printBill");
        }, 500);

        toast(this, "print_success", "success");
      } catch (error) {
        console.error("Error printing bill:", error);
        toast(this, "print_error", "error");
      }
    },

    async printOrderReceipt() {
      if (!this.order_table || !this.order_table.history?.length) {
        toast(this, "no_orders_to_print", "warning");
        return;
      }

      try {
        this.printBill = {
          ...this.order_table,
          table_id: this.selectedTable
            ? {
                id: this.selectedTable.id,
                name: this.selectedTable.name,
              }
            : this.order_table.table_id || this.order_table.table,
          user_id: this.user
            ? {
                id: this.user.id,
                first_name:
                  this.user.first_name || this.user.name || this.user.username,
              }
            : this.order_table.user_id,
        };

        setTimeout(() => {
          this.$htmlToPaper("printBill");
        }, 500);

        toast(this, "print_success", "success");
      } catch (error) {
        console.error("Error printing receipt:", error);
        toast(this, "print_error", "error");
      }
    },

    onDragStart(event) {
      this.isDragging = true;
      this.draggedTable = event.item;
      event.item.classList.add("dragging");

      event.item.style.transform = "rotate(2deg) scale(1.05)";
      event.item.style.boxShadow = "0 12px 24px rgba(0, 0, 0, 0.25)";
      event.item.style.zIndex = "1000";
    },

    onDragEnd(event) {
      this.isDragging = false;
      this.draggedTable = null;
      event.item.classList.remove("dragging");

      event.item.style.transform = "";
      event.item.style.boxShadow = "";
      event.item.style.zIndex = "";
    },

    async handleTableChange({ moved }) {
      if (!moved) {
        return;
      }

      const { newIndex, element } = moved;

      try {
        this.loading = true;

        const form = {
          oldIndex: element.position || 0,
          newIndex: newIndex + 1,
          id: element.id,
          store_id: this.user.stores.id,
          branch_id: this.user.branch_id,
          zone_id: element.zone_id,
        };

        const { data } = await this.$http.post("store_table_sort", form);

        if (data.status) {
          await toast(this, "table_position_updated");

          await this.fetchItem();
        } else {
          throw new Error(data.message || "Failed to update table position");
        }

        this.loading = false;
      } catch (error) {
        toast(this, "table_position_update_error", "error");
        this.loading = false;

        await this.fetchItem();
      }
    },

    async saveTablePosition(tableId, positionIndex) {
      try {
        const response = await this.$http.put(`store_table/${tableId}`, {
          position: positionIndex,
        });

        return response.data;
      } catch (error) {
        throw error;
      }
    },

    async saveTablePositions(positionUpdates) {
      try {
        const promises = positionUpdates.map((update) => {
          return this.$http.put(`store_table/${update.id}`, {
            position: update.position,
          });
        });

        const results = await Promise.all(promises);
        return results;
      } catch (error) {
        throw error;
      }
    },

    toggleDragMode() {
      this.dragEnabled = !this.dragEnabled;
      if (!this.dragEnabled) {
        this.isDragging = false;
        this.draggedTable = null;
      }
    },

    clearFilters() {
      this.search = "";
      this.zone_filter = "";
      this.status_filter = "";
      this.fetchItem();
    },

    newItem() {
      this.update = false;
      this.dialogAdd = true;
      this.form = {
        name: "",
        note: "",
        status: "available",
        zone_id: null,
        seats: 4,
      };
    },

    addOrder() {
      if (!this.selectedTable) {
        toast(this, "Please select a table first", "warning");
        return;
      }

      this.$router.push({
        name: "sale",
        query: {
          tableId: this.selectedTable.id,
          tableName: this.selectedTable.name,
        },
      });
    },

    updateAllSelected(value) {
      this.allSelected = value;
      if (this.order_table && this.order_table.history) {
        this.order_table.history.forEach((item) => {
          this.$set(item, "selected", value);
        });
      }
    },

    updateItemSelection(item, value) {
      this.$set(item, "selected", value);

      if (this.order_table && this.order_table.history) {
        const selectedItems = this.order_table.history.filter(
          (i) => i.selected
        );
        this.allSelected =
          selectedItems.length === this.order_table.history.length;
      }
    },

    formatNumber(val) {
      if (val === null || val === undefined) return "0";
      if (typeof val === "string") {
        val = parseFloat(val);
      }

      if (val % 1 === 0) {
        return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      } else {
        return val
          .toFixed(2)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
    },
  },
};
